import { useMemo, useState } from 'react';
import { allHeroes } from '../../game/data/heroes';
import { getTribeDefinition } from '../../game/data/tribes';
import { useHeroDetailStore } from '../../stores/heroDetail';
import { bustPath } from '../../components/portraitArt';
import { GalleryShell, GallerySection } from '../../components/GalleryShell';
import { EDGES } from '../../theme/tokens';

// Every hero in the game, browsable outside a match.
//
// Hero select shows you four at a time and asks you to commit in a few
// seconds; nothing in the product ever let a player just READ the roster. The
// faction split (cholo banks, rocker burns) and the tribe verbs are the two
// systems the game leans on hardest and the two it explains least, so this
// groups by faction and states the axis rather than listing 16 cards.
//
// Benched heroes are excluded on purpose: `disabled: true` means it cannot
// appear in a draft, and a catalogue that shows heroes you can never play is
// a bug report waiting to happen.

const FACTION_BLURB: Record<string, string> = {
  cholo:
    'Cholo heroes BANK. Permanent gains, compounding boards, and payoffs bought by not spending.',
  emo:
    'Rocker heroes BURN. Huge this-combat spikes and tempo that vanishes when the set ends.',
};

export function HeroesPage() {
  const [filter, setFilter] = useState('all');
  const openHero = useHeroDetailStore((s) => s.open);

  const live = useMemo(
    () => allHeroes.filter((h) => !(h as { disabled?: boolean }).disabled),
    [],
  );

  const filters = useMemo(() => {
    const cholo = live.filter((h) => h.faction === 'cholo').length;
    const emo = live.filter((h) => h.faction === 'emo').length;
    return [
      { id: 'all', label: 'All', hint: String(live.length) },
      { id: 'cholo', label: 'Cholos', hint: String(cholo) },
      { id: 'emo', label: 'Rockers', hint: String(emo) },
    ];
  }, [live]);

  const groups = useMemo(() => {
    const wanted = filter === 'all' ? ['cholo', 'emo'] : [filter];
    return wanted.map((f) => ({
      faction: f,
      heroes: live.filter((h) => h.faction === f),
    }));
  }, [live, filter]);

  return (
    <GalleryShell
      eyebrow="codex"
      title="Heroes"
      blurb="Every headliner you can draft. Tap one for the full portrait, its power, and the tribe it pulls toward."
      filters={filters}
      activeFilter={filter}
      onFilter={setFilter}
    >
      {groups.map(({ faction, heroes }) => (
        <GallerySection
          key={faction}
          label={faction === 'cholo' ? 'Cholos' : 'Rockers'}
          sublabel={FACTION_BLURB[faction]}
          accent={faction === 'cholo' ? '#ff8a3d' : '#ff2e7e'}
        >
          <div className="grid grid-cols-2 gap-sm sm:grid-cols-3 lg:grid-cols-4">
            {heroes.map((hero) => {
              const tribe = hero.tribe ? getTribeDefinition(hero.tribe) : undefined;
              const tribeColor = tribe
                ? `#${tribe.color.toString(16).padStart(6, '0')}`
                : '#8a7e6c';
              const isActive = hero.powerType.startsWith('active_');
              return (
                <button
                  key={hero.id}
                  onClick={() => openHero(hero)}
                  className="group flex flex-col overflow-hidden rounded-md border bg-stucco1 text-left transition-colors hover:bg-stucco2"
                  style={{ borderColor: EDGES.soft }}
                >
                  {/* The square bust, never the full page — see portraitArt.ts. */}
                  <div className="relative aspect-square w-full overflow-hidden">
                    {hero.portrait ? (
                      <img
                        src={`${import.meta.env.BASE_URL}${bustPath(hero.portrait)}`}
                        alt=""
                        aria-hidden
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    ) : (
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ background: 'rgba(19,15,10,1)' }}
                      >
                        <span
                          className="font-data text-[11px] uppercase tracking-[0.2em]"
                          style={{ color: tribeColor }}
                        >
                          no art
                        </span>
                      </div>
                    )}
                    <span
                      className={`absolute right-[4px] top-[4px] rounded-sm px-xs py-[1px] font-data text-[10px] uppercase tracking-[0.15em] ${
                        isActive ? 'bg-sodium text-sharpie' : 'bg-asphalt/80 text-graphite'
                      }`}
                    >
                      {isActive ? `${hero.cost ?? 0}g` : 'passive'}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-col gap-[2px] p-sm">
                    <span
                      className={`truncate text-[15px] leading-tight ${
                        hero.faction === 'cholo' ? 'font-cholo' : 'font-emo'
                      }`}
                      style={{ color: tribeColor }}
                    >
                      {hero.name}
                    </span>
                    <span className="font-data text-[10px] uppercase tracking-[0.2em] text-graphite">
                      {tribe ? tribe.name : 'no tribe'}
                    </span>
                    <span className="mt-[2px] line-clamp-3 text-[11px] leading-snug text-marker">
                      {hero.powerDescription}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </GallerySection>
      ))}
    </GalleryShell>
  );
}
